import { useMemo, useRef, useState, type ReactNode } from 'react';
import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import type { Editor } from '@tiptap/react';
import {
  Archive,
  Check,
  CheckCircle2,
  Copy,
  ExternalLink,
  Loader2,
  Lock,
  Mic,
  MicOff,
  PencilLine,
  Receipt,
  Send,
  Sparkles,
  Undo2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/atoms/button';
import { Checkbox } from '@/components/atoms/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import { Input } from '@/components/atoms/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { Skeleton } from '@/components/atoms/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/atoms/table';
import { Textarea } from '@/components/atoms/textarea';
import {
  useApproveReport,
  useArchiveReport,
  useBeginReportRevision,
  useCreateReportSection,
  useDeleteReportSection,
  useDeleteReportPhoto,
  useDiscardReportRevision,
  usePublishReport,
  useReorderReportSections,
  useReport,
  useReportFields,
  useReportRevisions,
  useReportShareInfo,
  useRotateReportShare,
  useSendBackReport,
  useSubmitReportForReview,
  useSuggestRecommendations,
  useSuggestReportSummary,
  useTranscribeAudio,
  useUnsignReport,
  useUpdateReportPhoto,
  useUpdateReportSection,
  useUploadReportPhoto,
  type ReportRevisionSummary,
  type ReportShareAccessMode,
  type SuggestedRecommendation,
} from '@/hooks/reports/useReports';
import { DEFAULT_ESTIMATE_LIST_FILTERS } from '@/components/organisms/estimates/support/estimate-list-filters';
import {
  ReportDocEditor,
  type SectionSaveStatus,
} from '@/features/reports/doc-editor/ReportDocEditor';
import { SectionSidebar } from '@/features/reports/doc-editor/SectionSidebar';
import {
  ReportDocEditorContext,
  type ReportDocEditorEnv,
  type ReportDocPhoto,
} from '@/features/reports/doc-editor/context';
import { countPhotoReferences } from '@/features/reports/doc-editor/support/photo-references';
import {
  collectLineItems,
  lineItemsSubtotal,
  toReportDoc,
  type ReportDoc,
  type ReportDocLineItem,
} from '@/features/reports/doc-editor/schema';
import type { ReportSectionWire } from '@/lib/api/types/reports';
import { useCompanyTimezone } from '@/hooks/settings/useCompanyTimezone';
import { formatDateTimeInTimeZone } from '@/lib/date-values';
import { reportKeys } from '@/lib/queryKeys';

const ESTIMATE_DETAIL_SEARCH = {
  ...DEFAULT_ESTIMATE_LIST_FILTERS,
  tab: 'overview' as const,
};

export const Route = createFileRoute('/_dashboard/dashboard/reports/$id')({
  component: ReportDetailPage,
});

interface ReportPhotoWire {
  id: string;
  storageUrl: string;
  thumbnailUrl: string;
  caption: string | null;
  annotationLayer?: unknown;
}
interface ReportDetail {
  id: string;
  number: string;
  title: string;
  status: string;
  sections: ReportSectionWire[];
  photos: ReportPhotoWire[];
  photoCount: number;
  shareToken: string | null;
  spawnedEstimateId: string | null;
  publishedAt: string | null;
  /** Set while a working revision is open on a published report (spec §3.4). */
  reviseStartedAt: string | null;
  template?: { stateFlow?: { requiresReview?: boolean } | null } | null;
}

const STATUS_STYLES: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  IN_PROGRESS: 'bg-blue-100 text-blue-700',
  REVIEW: 'bg-amber-100 text-amber-700',
  PUBLISHED: 'bg-emerald-100 text-emerald-700',
  SIGNED: 'bg-violet-100 text-violet-700',
  ARCHIVED: 'bg-stone-100 text-stone-500',
};

function ReportStatusBadge({ status }: { status: string }) {
  const cls = STATUS_STYLES[status] ?? STATUS_STYLES.DRAFT;
  return (
    <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${cls}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

function buildShareUrl(token: string): string {
  return `${window.location.origin}/r/${token}`;
}

function sectionAnchorId(sectionId: string): string {
  return `report-section-${sectionId}`;
}

function ReportDetailPage() {
  const { id } = useParams({ from: '/_dashboard/dashboard/reports/$id' });
  const queryClient = useQueryClient();
  const { data: report, isLoading } = useReport(id);
  const publish = usePublishReport(id);
  const beginRevision = useBeginReportRevision(id);
  const discardRevision = useDiscardReportRevision(id);
  const unsign = useUnsignReport(id);
  const archive = useArchiveReport(id);
  const submitForReview = useSubmitReportForReview(id);
  const approve = useApproveReport(id);
  const sendBack = useSendBackReport(id);
  const createSection = useCreateReportSection(id);
  const updateSection = useUpdateReportSection(id);
  const deleteSection = useDeleteReportSection(id);
  const reorderSections = useReorderReportSections(id);
  const uploadPhoto = useUploadReportPhoto(id);
  const updatePhoto = useUpdateReportPhoto(id);
  const deletePhoto = useDeleteReportPhoto(id);
  const suggestSummary = useSuggestReportSummary(id);
  const suggestRecommendations = useSuggestRecommendations(id);
  const revisions = useReportRevisions(id);
  const fields = useReportFields(id);
  const [publishOpen, setPublishOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [sendBackOpen, setSendBackOpen] = useState(false);
  const [statuses, setStatuses] = useState<Record<string, SectionSaveStatus>>({});
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<SuggestedRecommendation[]>([]);
  // Photos confirmed this session — visible to image nodes before the report
  // detail refetch lands.
  const [localPhotos, setLocalPhotos] = useState<Map<string, ReportDocPhoto>>(new Map());
  const timeZone = useCompanyTimezone();

  // Live editors by section id — AI assists insert at the cursor of the
  // focused one; the publish preview reads unsaved lineItems from them.
  const editorsRef = useRef(new Map<string, Editor>());
  const flushersRef = useRef(new Map<string, () => Promise<void>>());

  // Persist every mounted section editor's pending (debounced) edits before a
  // lifecycle transition reads the sections back from the database, so the
  // published revision, PDF, share link, and spawned estimate never miss the
  // author's last keystrokes.
  const flushAllEditors = async () => {
    await Promise.all(Array.from(flushersRef.current.values()).map((flush) => flush()));
  };
  const focusedSectionIdRef = useRef<string | null>(null);

  const r = report as ReportDetail | undefined;
  // Spec §3.4 — a published report unlocks once the author opens a working
  // revision; customers keep seeing the published version until republish.
  const isRevising = r?.status === 'PUBLISHED' && Boolean(r.reviseStartedAt);
  const isEditable = r?.status === 'DRAFT' || r?.status === 'IN_PROGRESS' || isRevising;

  const orderedSections = useMemo(
    () => [...(r?.sections ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [r?.sections],
  );

  const photosById = useMemo(() => {
    const map = new Map<string, ReportDocPhoto>();
    for (const p of r?.photos ?? []) map.set(p.id, p);
    for (const [pid, p] of localPhotos) if (!map.has(pid)) map.set(pid, p);
    return map;
  }, [r?.photos, localPhotos]);

  const env = useMemo<ReportDocEditorEnv>(
    () => ({
      reportId: id,
      readOnly: !isEditable,
      photoUploadsEnabled: true,
      // Live values, so a chip reads "Acme HVAC" rather than {{COMPANY_NAME}}.
      fields: fields.data ?? [],
      fieldsHaveValues: true,
      photosById,
      getPhotoReferenceCount: (photoId) =>
        orderedSections.reduce((count, section) => {
          // Read every mounted editor at delete time so an unsaved copy in a
          // different section protects the shared asset. Unmounted sections
          // fall back to the latest server snapshot.
          const editor = editorsRef.current.get(section.id);
          const content = editor ? editor.getJSON() : section.content;
          return count + countPhotoReferences(content, photoId);
        }, 0),
      uploadPhoto: async (file: File) => {
        const photo = (await uploadPhoto.mutateAsync({
          file,
          sectionId: focusedSectionIdRef.current ?? undefined,
        })) as ReportDocPhoto;
        setLocalPhotos((prev) => new Map(prev).set(photo.id, photo));
        return photo;
      },
      updatePhoto: async (photoId, patch) => {
        await updatePhoto.mutateAsync({ photoId, ...patch });
        setLocalPhotos((prev) => {
          const existing = prev.get(photoId);
          if (!existing) return prev;
          return new Map(prev).set(photoId, {
            ...existing,
            ...(patch.caption !== undefined ? { caption: patch.caption } : {}),
            ...(patch.annotationLayer !== undefined
              ? { annotationLayer: patch.annotationLayer }
              : {}),
          });
        });
      },
      deletePhoto: async (photoId) => {
        await deletePhoto.mutateAsync(photoId);
        setLocalPhotos((prev) => {
          const next = new Map(prev);
          next.delete(photoId);
          return next;
        });
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, isEditable, orderedSections, photosById, fields.data],
  );

  if (isLoading) {
    return (
      <div className="mx-auto max-w-5xl space-y-4 p-6">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }
  if (!r) {
    return <div className="p-6">Report not found.</div>;
  }

  const isPublished = r.status === 'PUBLISHED' || r.status === 'SIGNED';
  const isDraftStage = r.status === 'DRAFT' || r.status === 'IN_PROGRESS';
  const inReview = r.status === 'REVIEW';
  const requiresReview = r.template?.stateFlow?.requiresReview === true;
  // Revisions come back newest-first. Submit-for-review also snapshots, so the
  // head is not necessarily a published version: label the report with the
  // newest *published* revision, and number the next one off the head.
  const publishedRevisions = (revisions.data ?? []).filter((rev) => rev.publishedAt);
  const publishedVersion = publishedRevisions[0]?.revisionNumber ?? null;
  const nextVersion = (revisions.data?.[0]?.revisionNumber ?? 0) + 1;

  /** Line items across all sections — live editor state wins over the cache. */
  const gatherLineItems = (): ReportDocLineItem[] =>
    orderedSections.flatMap((section) => {
      const editor = editorsRef.current.get(section.id);
      const doc = editor
        ? (editor.getJSON() as unknown as ReportDoc)
        : toReportDoc(section.content);
      return collectLineItems(doc);
    });
  const recommendationLines = gatherLineItems();

  const focusedEditor = (): Editor | null => {
    const focused = focusedSectionIdRef.current
      ? editorsRef.current.get(focusedSectionIdRef.current)
      : null;
    if (focused) return focused;
    for (const section of orderedSections) {
      const editor = editorsRef.current.get(section.id);
      if (editor) return editor;
    }
    return null;
  };

  const onPublish = async (changeSummary?: string) => {
    try {
      await flushAllEditors();
      await publish.mutateAsync(changeSummary);
      setPublishOpen(false);
      toast.success(
        isRevising
          ? 'New version published — the customer link now shows it'
          : recommendationLines.length > 0
            ? 'Report published and estimate spawned'
            : 'Report published',
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not publish report');
    }
  };

  /**
   * Spec §3.4 — unlock a published report for edits. Customers keep seeing the
   * published version until the author republishes.
   */
  const onBeginRevision = async () => {
    try {
      await beginRevision.mutateAsync();
      toast.success('Editing a new version — customers still see the published one');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not start a new version');
    }
  };

  const onDiscardRevision = async () => {
    if (
      !window.confirm(
        'Discard the edits in this version? The report reverts to the published version.',
      )
    ) {
      return;
    }
    try {
      await discardRevision.mutateAsync();
      toast.success('Edits discarded — back to the published version');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not discard the edits');
    }
  };

  const onUnsign = async () => {
    if (
      !window.confirm(
        'Unsign this report so it can be edited? The captured signature is kept as a record and the customer will need to sign the new version.',
      )
    ) {
      return;
    }
    try {
      await unsign.mutateAsync();
      toast.success('Report unsigned — you can now edit and republish it');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not unsign the report');
    }
  };
  const onArchive = async () => {
    try {
      await archive.mutateAsync();
      toast.success('Report archived');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not archive report');
    }
  };

  const onSubmitForReview = async () => {
    try {
      await flushAllEditors();
      await submitForReview.mutateAsync(undefined);
      toast.success('Submitted for office review');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not submit for review');
    }
  };

  const onApprove = async () => {
    try {
      await approve.mutateAsync();
      toast.success(
        recommendationLines.length > 0 ? 'Approved — estimate spawning' : 'Approved and published',
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not approve');
    }
  };

  const onSendBack = async (reason: string) => {
    try {
      await sendBack.mutateAsync(reason);
      setSendBackOpen(false);
      toast.success('Sent back to the author');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not send back');
    }
  };

  // ── Section CRUD ──────────────────────────────────────────────────────
  const onSelectSection = (sectionId: string) => {
    setActiveSectionId(sectionId);
    document
      .getElementById(sectionAnchorId(sectionId))
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    editorsRef.current.get(sectionId)?.commands.focus();
  };

  const onAddSection = async (title: string) => {
    try {
      await createSection.mutateAsync({ title });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not add section');
    }
  };

  const onRenameSection = async (sectionId: string, title: string) => {
    try {
      await updateSection.mutateAsync({ sectionId, title });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not rename section');
    }
  };

  const onDeleteSection = async (sectionId: string) => {
    const section = orderedSections.find((s) => s.id === sectionId);
    if (
      !window.confirm(
        `Delete section "${section?.title ?? 'Untitled'}"? Its content is removed from the report.`,
      )
    ) {
      return;
    }
    try {
      await deleteSection.mutateAsync(sectionId);
      // Drop the editor/flush handles only after the delete succeeds; the
      // section stays mounted (and functional) if the request fails.
      editorsRef.current.delete(sectionId);
      flushersRef.current.delete(sectionId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete section');
    }
  };

  const onReorderSections = (sectionIds: string[]) => {
    reorderSections.mutate(sectionIds);
  };

  // ── AI assists ────────────────────────────────────────────────────────
  const insertTranscript = (text: string) => {
    const editor = focusedEditor();
    if (!editor) {
      toast.info('Add a section first, then dictate into it.');
      return;
    }
    editor.chain().focus().insertContent(text).run();
  };

  const onSuggestSummary = async () => {
    const first = orderedSections[0];
    const editor = first ? editorsRef.current.get(first.id) : null;
    if (!editor) {
      toast.info('Add a section first — the summary fills the top of the report.');
      return;
    }
    try {
      const res = await suggestSummary.mutateAsync(undefined);
      const summary = res.summary?.trim();
      if (!summary) {
        toast.info('AI returned no summary — try adding more detail first.');
        return;
      }
      editor
        .chain()
        .focus()
        .insertContentAt(0, {
          type: 'paragraph',
          content: [{ type: 'text', text: summary }],
        })
        .run();
      toast.success('Summary added to the top of the report');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Summary suggestion failed');
    }
  };

  const onSuggestRecommendations = async () => {
    try {
      const res = await suggestRecommendations.mutateAsync(undefined);
      const usable = res.items.filter((s) => s.pricebookItemId);
      if (usable.length === 0) {
        toast.info('No suggestions — try adding more detail to the report first.');
        return;
      }
      setSuggestions(usable);
      setSuggestionsOpen(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not fetch suggestions');
    }
  };

  /** Merge accepted AI suggestions into the report's lineItems node. */
  const acceptSuggestions = (picked: SuggestedRecommendation[]) => {
    setSuggestionsOpen(false);
    const additions: ReportDocLineItem[] = picked
      .filter((s) => s.pricebookItemId)
      .map((s) => ({
        pricebookItemId: s.pricebookItemId as string,
        name: s.name ?? '(AI suggestion)',
        description: s.reason,
        quantity: s.qty || 1,
        unitPrice: s.suggestedPrice ?? 0,
      }));
    if (additions.length === 0) return;

    // Prefer merging into the first existing lineItems node anywhere in the doc.
    for (const section of orderedSections) {
      const editor = editorsRef.current.get(section.id);
      if (!editor) continue;
      let found: { pos: number; items: ReportDocLineItem[] } | null = null;
      editor.state.doc.descendants((node, pos) => {
        if (found) return false;
        if (node.type.name === 'lineItems') {
          found = {
            pos,
            items: Array.isArray(node.attrs.items) ? (node.attrs.items as ReportDocLineItem[]) : [],
          };
          return false;
        }
        return true;
      });
      if (found) {
        const { pos, items } = found as { pos: number; items: ReportDocLineItem[] };
        const existingIds = new Set(items.map((i) => i.pricebookItemId).filter(Boolean));
        const merged = [
          ...items,
          ...additions.filter((a) => !a.pricebookItemId || !existingIds.has(a.pricebookItemId)),
        ];
        const addedCount = merged.length - items.length;
        if (addedCount === 0) {
          toast.info('All selected items were already on the recommendation.');
          return;
        }
        editor
          .chain()
          .command(({ tr }) => {
            tr.setNodeMarkup(pos, undefined, { items: merged });
            return true;
          })
          .run();
        toast.success(`Added ${addedCount} suggested item${addedCount === 1 ? '' : 's'}`);
        return;
      }
    }

    // No lineItems node yet — append one to the focused (or last) section.
    const editor =
      focusedEditor() ??
      [...orderedSections]
        .reverse()
        .map((s) => editorsRef.current.get(s.id))
        .find(Boolean) ??
      null;
    if (!editor) {
      toast.error('Add a section first, then re-apply the suggestions.');
      return;
    }
    editor
      .chain()
      .focus('end')
      .insertContent({ type: 'lineItems', attrs: { items: additions } })
      .run();
    toast.success(`Added ${additions.length} suggested item${additions.length === 1 ? '' : 's'}`);
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="font-mono text-xs text-muted-foreground">{r.number}</p>
          <h1 className="text-2xl font-bold">{r.title}</h1>
          <div className="mt-2 flex items-center gap-2">
            <ReportStatusBadge status={r.status} />
            {isRevising ? (
              <span className="inline-flex items-center rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                Editing v{nextVersion} — draft
              </span>
            ) : null}
            {r.publishedAt ? (
              <span className="text-xs text-muted-foreground">
                {publishedVersion ? `v${publishedVersion} published ` : 'Published '}
                {formatDateTimeInTimeZone(r.publishedAt, timeZone, {
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                })}
              </span>
            ) : null}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {isDraftStage && requiresReview && (
            <Button onClick={onSubmitForReview} disabled={submitForReview.isPending}>
              {submitForReview.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Send className="size-4" />
              )}
              Submit for Review
            </Button>
          )}
          {isDraftStage && !requiresReview && (
            <Button onClick={() => setPublishOpen(true)} disabled={publish.isPending}>
              <Send className="size-4" />
              Publish
            </Button>
          )}
          {isRevising && (
            <>
              <Button onClick={() => setPublishOpen(true)} disabled={publish.isPending}>
                <Send className="size-4" />
                Republish as v{nextVersion}
              </Button>
              <Button
                variant="outline"
                onClick={onDiscardRevision}
                disabled={discardRevision.isPending}
              >
                {discardRevision.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Undo2 className="size-4" />
                )}
                Discard changes
              </Button>
            </>
          )}
          {r.status === 'PUBLISHED' && !isRevising && (
            <Button onClick={onBeginRevision} disabled={beginRevision.isPending}>
              {beginRevision.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <PencilLine className="size-4" />
              )}
              Edit &amp; create v{nextVersion}
            </Button>
          )}
          {r.status === 'SIGNED' && (
            <Button variant="outline" onClick={onUnsign} disabled={unsign.isPending}>
              {unsign.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <PencilLine className="size-4" />
              )}
              Unsign to edit
            </Button>
          )}
          {inReview && (
            <>
              <Button onClick={onApprove} disabled={approve.isPending}>
                {approve.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="size-4" />
                )}
                Approve & Publish
              </Button>
              <Button
                variant="outline"
                onClick={() => setSendBackOpen(true)}
                disabled={sendBack.isPending}
              >
                <Undo2 className="size-4" />
                Send Back
              </Button>
            </>
          )}
          {r.status !== 'ARCHIVED' && (
            <Button variant="outline" onClick={onArchive} disabled={archive.isPending}>
              <Archive className="size-4" />
              Archive
            </Button>
          )}
        </div>
      </header>

      {/*
        The single most confusing moment in this screen used to be opening a
        published report: the toolbar was gone, typing silently failed, and
        nothing said why or how to proceed. These banners answer both.
      */}
      {isRevising ? (
        <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <PencilLine className="size-4 shrink-0" />
          <span>
            You are editing <strong>v{nextVersion}</strong>. Customers still see v
            {publishedVersion ?? 1} until you republish.
          </span>
          <button
            type="button"
            onClick={() => setHistoryOpen(true)}
            className="ml-auto text-xs font-medium underline underline-offset-2"
          >
            Version history
          </button>
        </div>
      ) : isPublished ? (
        <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border bg-muted/40 px-4 py-3 text-sm">
          <Lock className="size-4 shrink-0 text-muted-foreground" />
          <span className="text-muted-foreground">
            {r.status === 'SIGNED'
              ? 'This report is signed, so it is read-only. Unsign it to make corrections — the signature is kept as a record.'
              : `Published reports are read-only. Choose "Edit & create v${nextVersion}" to make corrections; customers keep seeing v${publishedVersion ?? 1} until you republish.`}
          </span>
          <button
            type="button"
            onClick={() => setHistoryOpen(true)}
            className="ml-auto text-xs font-medium underline underline-offset-2"
          >
            Version history
          </button>
        </div>
      ) : null}

      <VersionHistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        revisions={publishedRevisions}
        isLoading={revisions.isLoading}
        timeZone={timeZone}
      />

      {isPublished && r.shareToken ? (
        <ShareSection
          reportId={r.id}
          shareToken={r.shareToken}
          spawnedEstimateId={r.spawnedEstimateId}
        />
      ) : null}

      <PublishConfirmDialog
        open={publishOpen}
        onOpenChange={setPublishOpen}
        lines={recommendationLines}
        isPending={publish.isPending}
        onConfirm={onPublish}
        republishAsVersion={isRevising ? nextVersion : undefined}
      />

      <SendBackDialog
        open={sendBackOpen}
        onOpenChange={setSendBackOpen}
        isPending={sendBack.isPending}
        onConfirm={onSendBack}
      />

      <SuggestionsDialog
        open={suggestionsOpen}
        onOpenChange={setSuggestionsOpen}
        suggestions={suggestions}
        onAccept={acceptSuggestions}
      />

      <ReportDocEditorContext.Provider value={env}>
        <div className="flex items-start gap-6">
          <aside className="hidden w-60 shrink-0 md:block">
            <div className="sticky top-4 space-y-5">
              <SectionSidebar
                sections={orderedSections.map((s) => ({ id: s.id, title: s.title }))}
                statuses={statuses}
                disabled={!isEditable}
                activeSectionId={activeSectionId}
                onSelect={onSelectSection}
                onAdd={onAddSection}
                onRename={onRenameSection}
                onDelete={onDeleteSection}
                onReorder={onReorderSections}
              />
              {isEditable ? (
                <div className="space-y-1 border-t pt-3">
                  <p className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    AI assist
                  </p>
                  <VoiceToTextButton reportId={r.id} onTranscribed={insertTranscript} />
                  <button
                    type="button"
                    onClick={onSuggestSummary}
                    disabled={suggestSummary.isPending}
                    className="flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:bg-accent/50 hover:text-foreground disabled:opacity-50"
                  >
                    {suggestSummary.isPending ? (
                      <Loader2 className="size-3.5 animate-spin" />
                    ) : (
                      <Sparkles className="size-3.5" />
                    )}
                    Suggest summary
                  </button>
                  <button
                    type="button"
                    onClick={onSuggestRecommendations}
                    disabled={suggestRecommendations.isPending}
                    className="flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:bg-accent/50 hover:text-foreground disabled:opacity-50"
                  >
                    {suggestRecommendations.isPending ? (
                      <Loader2 className="size-3.5 animate-spin" />
                    ) : (
                      <Receipt className="size-3.5" />
                    )}
                    Suggest recommendations
                  </button>
                </div>
              ) : null}
            </div>
          </aside>

          <div className="min-w-0 flex-1 space-y-6">
            {orderedSections.map((section) => (
              <section
                key={section.id}
                id={sectionAnchorId(section.id)}
                className="scroll-mt-4 rounded-xl border"
              >
                <div className="border-b px-4 py-2.5">
                  <h2 className="text-base font-semibold">{section.title}</h2>
                  {section.subtitle ? (
                    <p className="text-xs text-muted-foreground">{section.subtitle}</p>
                  ) : null}
                </div>
                <ReportDocEditor
                  sectionId={section.id}
                  initialContent={section.content}
                  onSaveContent={(content) =>
                    updateSection.mutateAsync({ sectionId: section.id, content })
                  }
                  onStatusChange={(status) =>
                    setStatuses((prev) =>
                      prev[section.id] === status ? prev : { ...prev, [section.id]: status },
                    )
                  }
                  onEditorReady={(editor) => {
                    if (editor) editorsRef.current.set(section.id, editor);
                    else editorsRef.current.delete(section.id);
                  }}
                  onRegisterFlush={(flush) => {
                    if (flush) flushersRef.current.set(section.id, flush);
                    else flushersRef.current.delete(section.id);
                  }}
                  onFocusSection={() => {
                    focusedSectionIdRef.current = section.id;
                    setActiveSectionId(section.id);
                  }}
                  // The server refused the save — most often because the report
                  // locked (published, archived, or republished from another
                  // tab). Refetch so the editor flips to read-only and the
                  // banner explains the way forward instead of the same toast
                  // firing again on every later keystroke.
                  onSaveRejected={() => {
                    void queryClient.invalidateQueries({
                      queryKey: reportKeys.detail(id),
                    });
                  }}
                />
              </section>
            ))}
            {orderedSections.length === 0 ? (
              <div className="rounded-xl border border-dashed p-10 text-center text-sm text-muted-foreground">
                This report has no sections yet.
                {isEditable ? ' Use "Add section" in the sidebar to start writing.' : ''}
              </div>
            ) : null}
          </div>
        </div>
      </ReportDocEditorContext.Provider>
    </div>
  );
}

const ACCESS_MODE_LABELS: Record<ReportShareAccessMode, string> = {
  PUBLIC_LINK: 'Public link — anyone with the URL',
  EMAIL_GATED: 'Email-gated — customer enters email to view',
  PORTAL_ONLY: 'Portal only — must log into the customer portal',
};

function ShareSection({
  reportId,
  shareToken,
  spawnedEstimateId,
}: {
  reportId: string;
  shareToken: string;
  spawnedEstimateId: string | null;
}) {
  const [copied, setCopied] = useState(false);
  const url = buildShareUrl(shareToken);
  const info = useReportShareInfo(reportId);
  const rotate = useRotateReportShare(reportId);
  const timeZone = useCompanyTimezone();
  const currentMode: ReportShareAccessMode = info.data?.accessMode ?? 'PUBLIC_LINK';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };
  const onChangeMode = async (next: ReportShareAccessMode) => {
    if (next === currentMode) return;
    try {
      await rotate.mutateAsync({ accessMode: next });
      toast.success(`Access mode set to ${next.replace('_', ' ').toLowerCase()}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update access mode');
    }
  };
  return (
    <section className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50/50 p-4">
      <h2 className="text-sm font-semibold text-emerald-900">Share with customer</h2>
      <p className="mt-0.5 text-xs text-emerald-800/80">
        This is the public link the customer will see. Re-publishing rotates the token.
      </p>
      <div className="mt-3 flex items-center gap-2">
        <Input value={url} readOnly className="font-mono text-xs" />
        <Button variant="outline" size="sm" onClick={copy}>
          {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
        <a href={url} target="_blank" rel="noreferrer" className="inline-flex">
          <Button variant="outline" size="sm">
            <ExternalLink className="size-4" />
            Open
          </Button>
        </a>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <label className="text-xs font-medium text-emerald-900">Access</label>
        <Select
          value={currentMode}
          onValueChange={(v) => onChangeMode(v as ReportShareAccessMode)}
          disabled={rotate.isPending}
        >
          <SelectTrigger className="h-8 max-w-md text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(ACCESS_MODE_LABELS) as ReportShareAccessMode[]).map((m) => (
              <SelectItem key={m} value={m}>
                {ACCESS_MODE_LABELS[m]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {rotate.isPending ? <Loader2 className="size-4 animate-spin text-emerald-700" /> : null}
      </div>
      {info.data?.recentAccessCount ? (
        <p className="mt-2 text-xs text-emerald-800/80">
          Customer has viewed this report {info.data.recentAccessCount}{' '}
          {info.data.recentAccessCount === 1 ? 'time' : 'times'}
          {info.data.lastAccessedAt
            ? ` — last on ${formatDateTimeInTimeZone(info.data.lastAccessedAt, timeZone, {
                dateStyle: 'medium',
                timeStyle: 'short',
              })}`
            : ''}
          .
        </p>
      ) : null}
      {spawnedEstimateId ? (
        <div className="mt-3 flex items-center gap-2 rounded-md border border-emerald-200 bg-white px-3 py-2">
          <Receipt className="size-4 text-emerald-700" />
          <span className="text-sm">
            An estimate was spawned from this report's recommendation.
          </span>
          <Link
            to="/dashboard/estimates/$id"
            params={{ id: spawnedEstimateId }}
            search={ESTIMATE_DETAIL_SEARCH}
            className="ml-auto text-sm text-primary hover:underline"
          >
            Open estimate
          </Link>
        </div>
      ) : null}
    </section>
  );
}

function formatMoney(n: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

function PublishConfirmDialog({
  open,
  onOpenChange,
  lines,
  isPending,
  onConfirm,
  republishAsVersion,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  lines: ReportDocLineItem[];
  isPending: boolean;
  onConfirm: (changeSummary?: string) => void;
  /** Version number this publish will mint, when republishing an edited report. */
  republishAsVersion?: number;
}) {
  const hasLines = lines.length > 0;
  const subtotal = lineItemsSubtotal(lines);
  const isRepublish = republishAsVersion !== undefined;
  const [changeSummary, setChangeSummary] = useState('');

  const title = isRepublish
    ? `Republish as v${republishAsVersion}?`
    : hasLines
      ? 'Publish and spawn estimate?'
      : 'Publish report?';
  const description = isRepublish
    ? 'Your edits become the version customers see. The previous version stays in the history, and the old share link is replaced by a new one.'
    : hasLines
      ? 'Publishing will lock this report and create an estimate from your recommended items. The customer share link goes live immediately.'
      : 'Publishing locks this report and makes the customer share link live. You can correct mistakes later by creating a new version.';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {hasLines ? <RecommendationPreview lines={lines} subtotal={subtotal} /> : null}

        {isRepublish ? (
          <div className="space-y-1.5">
            <label htmlFor="report-change-summary" className="text-sm font-medium">
              What changed? <span className="text-muted-foreground">(optional)</span>
            </label>
            <Input
              id="report-change-summary"
              value={changeSummary}
              onChange={(e) => setChangeSummary(e.target.value)}
              placeholder="Replaced the wrong condenser photo"
            />
            <p className="text-xs text-muted-foreground">Shown in this report&apos;s history.</p>
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={() => onConfirm(changeSummary.trim() || undefined)} disabled={isPending}>
            {isPending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
            {isRepublish
              ? `Republish as v${republishAsVersion}`
              : hasLines
                ? 'Publish & spawn estimate'
                : 'Publish'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Spec §3.4 — the append-only publish log. Each row is one published version;
 * the newest is what the customer's share link resolves to. Internal
 * submit-for-review snapshots are filtered out by the caller — they were never
 * a version anyone outside the company saw.
 */
function VersionHistoryDialog({
  open,
  onOpenChange,
  revisions,
  isLoading,
  timeZone,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  revisions: ReportRevisionSummary[];
  isLoading: boolean;
  timeZone: string | undefined;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Version history</DialogTitle>
          <DialogDescription>
            Every publish creates a version. Customers always see the newest one.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : revisions.length === 0 ? (
          <p className="py-4 text-sm text-muted-foreground">
            This report has not been published yet.
          </p>
        ) : (
          <ul className="divide-y rounded-md border">
            {revisions.map((rev, index) => (
              <li key={rev.id} className="flex items-baseline gap-3 px-3 py-2 text-sm">
                <span className="font-medium tabular-nums">v{rev.revisionNumber}</span>
                <span className="min-w-0 flex-1 truncate">{rev.changeSummary || 'Published'}</span>
                {index === 0 ? (
                  <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700">
                    Current
                  </span>
                ) : null}
                <span className="shrink-0 text-xs text-muted-foreground">
                  {formatDateTimeInTimeZone(rev.publishedAt ?? rev.createdAt, timeZone, {
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                  })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Preview of the lines `EstimateSpawnService` will copy onto the spawned
 * estimate (gathered from every `lineItems` node across the sections), so
 * publishing never produces a "wait, an estimate appeared out of nowhere"
 * surprise.
 */
function RecommendationPreview({
  lines,
  subtotal,
}: {
  lines: ReportDocLineItem[];
  subtotal: number;
}): ReactNode {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Item</TableHead>
            <TableHead className="w-[60px] text-right">Qty</TableHead>
            <TableHead className="w-[110px] text-right">Total</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {lines.map((l, i) => (
            <TableRow key={`${l.pricebookItemId ?? l.name}-${i}`}>
              <TableCell className="font-medium whitespace-normal wrap-anywhere">
                {l.name}
              </TableCell>
              <TableCell className="text-right tabular-nums">{l.quantity}</TableCell>
              <TableCell className="text-right font-medium tabular-nums">
                {formatMoney(l.quantity * l.unitPrice)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="flex items-center justify-end gap-3 border-t bg-muted/30 px-4 py-2 text-sm">
        <span className="text-muted-foreground">Estimate total</span>
        <span className="font-semibold tabular-nums">{formatMoney(subtotal)}</span>
      </div>
    </div>
  );
}

function SendBackDialog({
  open,
  onOpenChange,
  isPending,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  isPending: boolean;
  onConfirm: (reason: string) => void | Promise<void>;
}) {
  const [reason, setReason] = useState('');
  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) setReason('');
        onOpenChange(v);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Send back to author?</DialogTitle>
          <DialogDescription>
            The author will see your note and can keep editing. The report stays in IN_PROGRESS
            state until they resubmit.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="What needs to change before this can be approved?"
          rows={4}
          autoFocus
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => onConfirm(reason.trim())}
            disabled={isPending || reason.trim().length === 0}
          >
            {isPending ? <Loader2 className="size-4 animate-spin" /> : <Undo2 className="size-4" />}
            Send back
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SuggestionsDialog({
  open,
  onOpenChange,
  suggestions,
  onAccept,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  suggestions: SuggestedRecommendation[];
  onAccept: (picked: SuggestedRecommendation[]) => void;
}) {
  const initialChecked = suggestions.map((s) => Boolean(s.pricebookItemId));
  const [checked, setChecked] = useState<boolean[]>(initialChecked);
  // Reset checks when a new batch arrives (the dialog reopens on every fetch).
  if (checked.length !== suggestions.length) setChecked(initialChecked);

  const toggle = (i: number, v: boolean) => {
    const next = [...checked];
    next[i] = v;
    setChecked(next);
  };

  const pickedCount = checked.filter(Boolean).length;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="size-4 text-amber-500" />
            Suggested items from this report
          </DialogTitle>
          <DialogDescription>
            Generated from the report's content. Suggestions are pulled from your pricebook —
            review, adjust, and add the ones you want. Nothing is added without your confirmation.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[420px] space-y-2 overflow-y-auto">
          {suggestions.map((s, i) => (
            <label
              key={`${s.pricebookItemId}-${i}`}
              className="flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors hover:bg-accent/40"
            >
              <Checkbox
                checked={checked[i] ?? false}
                onCheckedChange={(v) => toggle(i, Boolean(v))}
                className="mt-1"
              />
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex items-center gap-2">
                  {s.code ? <span className="font-mono text-xs">{s.code}</span> : null}
                  <span className="font-medium">{s.name ?? '(unrecognized item)'}</span>
                </div>
                <p className="text-sm text-muted-foreground">{s.reason}</p>
                <div className="flex gap-4 text-xs text-muted-foreground">
                  <span>Qty {s.qty}</span>
                  {s.suggestedPrice !== undefined ? (
                    <span>Price {formatMoney(s.suggestedPrice)}</span>
                  ) : null}
                </div>
              </div>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => onAccept(suggestions.filter((_, i) => checked[i]))}
            disabled={pickedCount === 0}
          >
            Add {pickedCount} item{pickedCount === 1 ? '' : 's'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VoiceToTextButton({
  reportId,
  onTranscribed,
}: {
  reportId: string;
  onTranscribed: (text: string) => void;
}) {
  const transcribe = useTranscribeAudio(reportId);
  const [recording, setRecording] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const start = async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      toast.error("This browser doesn't support audio capture.");
      return;
    }
    if (typeof MediaRecorder === 'undefined') {
      toast.error("This browser doesn't support MediaRecorder.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Pick a mime the browser actually supports — Whisper accepts any of these.
      const mime = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'].find((m) =>
        MediaRecorder.isTypeSupported(m),
      );
      const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        // Always stop the underlying mic stream so the browser's recording
        // indicator actually disappears.
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime ?? 'audio/webm' });
        if (blob.size === 0) {
          toast.info('Nothing recorded.');
          return;
        }
        try {
          const res = await transcribe.mutateAsync(blob);
          const t = res.text?.trim();
          if (!t) {
            toast.info("Couldn't transcribe — try again with clearer speech.");
            return;
          }
          onTranscribed(t);
          toast.success('Transcribed');
        } catch (err) {
          toast.error(err instanceof Error ? err.message : 'Transcription failed');
        }
      };
      recorder.start();
      recorderRef.current = recorder;
      setRecording(true);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? `Microphone access denied: ${err.message}`
          : 'Microphone access denied',
      );
    }
  };

  const stop = () => {
    recorderRef.current?.stop();
    recorderRef.current = null;
    setRecording(false);
  };

  if (recording) {
    return (
      <button
        type="button"
        onClick={stop}
        className="flex w-full items-center gap-1.5 rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100"
      >
        <span className="relative flex size-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
          <span className="relative inline-flex size-2 rounded-full bg-red-500" />
        </span>
        <MicOff className="size-3.5" />
        Stop recording
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={start}
      disabled={transcribe.isPending}
      className="flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:bg-accent/50 hover:text-foreground disabled:opacity-50"
    >
      {transcribe.isPending ? (
        <Loader2 className="size-3.5 animate-spin" />
      ) : (
        <Mic className="size-3.5" />
      )}
      Voice to text
    </button>
  );
}
